import { Fragment, useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileUp, CheckCircle2, AlertTriangle, XCircle,
  FileDown, History, Loader2, Scale, HelpCircle, Library, RefreshCw, TrendingUp,
  ChevronDown, ChevronRight,
} from "lucide-react";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IE", { style: "currency", currency: "EUR" }) : "—");
const localDate = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const YEARS = (() => {
  const y = new Date().getFullYear();
  const out = [];
  for (let i = y + 1; i >= 2024; i--) out.push(i);
  return out;
})();

const monthShift = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

function MonthYearPicker({ value, onChange, monthTestId, yearTestId }) {
  const [y, m] = value.split("-");
  const years = YEARS.includes(Number(y)) ? YEARS : [Number(y), ...YEARS];
  const optPrefix = monthTestId.replace("-select", "");
  const yearOptPrefix = yearTestId.replace("-select", "");
  return (
    <>
      <Select value={m} onValueChange={(mm) => onChange(`${y}-${mm}`)}>
        <SelectTrigger className="w-[150px]" data-testid={monthTestId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTH_NAMES.map((name, i) => (
            <SelectItem key={name} value={String(i + 1).padStart(2, "0")}
              data-testid={`${optPrefix}-opt-${String(i + 1).padStart(2, "0")}`}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={y} onValueChange={(yy) => onChange(`${yy}-${m}`)}>
        <SelectTrigger className="w-[110px]" data-testid={yearTestId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {years.map((yr) => (
            <SelectItem key={yr} value={String(yr)} data-testid={`${yearOptPrefix}-opt-${yr}`}>{yr}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
}

const STATUS_LABELS = {
  matched: "Matches",
  under_contribution: "Zurich took too little",
  over_contribution: "Zurich took too much",
  missing_from_zurich: "Missing from Zurich",
  unexpected_zurich_entry: "In Zurich, not in payslips",
  duplicate_entry: "Duplicate payslip rows",
  unmatched_employee_reference: "Unknown employee reference",
  manual_review_required: "Needs a manual look",
};
const RED = new Set(["under_contribution", "missing_from_zurich"]);

const LOG_BADGES = {
  added: { label: "Added", cls: "bg-emerald-600 text-white border-transparent" },
  duplicate: { label: "Duplicate — skipped", cls: "bg-muted text-muted-foreground border-border" },
  updated: { label: "Updated", cls: "bg-amber-500 text-white border-transparent" },
};

function DropZone({ title, subtitle, accept, multiple, onFiles, done, doneText, busy, testId }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  return (
    <div
      className={`relative rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors
        ${drag ? "border-foreground bg-muted/60" : done ? "border-emerald-300 bg-emerald-50/60" : "border-border hover:border-foreground/40 hover:bg-muted/30"}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault(); setDrag(false);
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length) onFiles(multiple ? files : [files[0]]);
      }}
      data-testid={testId}
    >
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onFiles(files);
          e.target.value = "";
        }} />
      {busy ? (
        <Loader2 className="w-7 h-7 mx-auto mb-2 animate-spin text-muted-foreground" />
      ) : done ? (
        <CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-emerald-600" />
      ) : (
        <FileUp className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
      )}
      <div className="font-medium text-sm">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{done ? doneText : subtitle}</div>
    </div>
  );
}

function WeekStrip({ coverage }) {
  if (!coverage?.weeks?.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-muted-foreground">
        Weeks covered: <b>{coverage.weeks_covered}/{coverage.weeks_total}</b>
        {coverage.gap_count > 0 && <span className="text-amber-700"> · {coverage.gap_count} missing</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {coverage.weeks.map((w) => {
          const s = localDate(w.week_end).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
          return (
            <span key={w.week_start}
              className={`text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1
                ${w.covered ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
              data-testid={`check-week-${w.week_start}`}>
              {w.covered ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              w/e {s}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function UploadLog({ items }) {
  if (!items.length) return (
    <div className="text-xs text-muted-foreground px-1 py-2">No payslip files uploaded yet.</div>
  );
  const weeksLabel = (it) => {
    const d = it.pay_dates || [];
    if (!d.length) return "—";
    const f = (x) => localDate(x).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    return d.length === 1 ? `Week of ${f(d[0])}` : `${f(d[0])} – ${f(d[d.length - 1])} · ${d.length} weeks`;
  };
  return (
    <div className="overflow-x-auto" data-testid="check-upload-log">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th className="px-2 py-1.5 font-medium">When</th>
            <th className="px-2 py-1.5 font-medium">File</th>
            <th className="px-2 py-1.5 font-medium">Weeks</th>
            <th className="px-2 py-1.5 font-medium text-right">Rows</th>
            <th className="px-2 py-1.5 font-medium">Result</th>
            <th className="px-2 py-1.5 font-medium">By</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const b = LOG_BADGES[it.status] || { label: it.status, cls: "" };
            return (
              <tr key={it.id} className="border-t border-border" data-testid={`check-log-row-${i}`}>
                <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">
                  {new Date(it.uploaded_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}{" "}
                  {new Date(it.uploaded_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                </td>
                <td className="px-2 py-1.5 max-w-[220px] truncate font-medium">{it.filename}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">{weeksLabel(it)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{it.rows}</td>
                <td className="px-2 py-1.5">
                  <Badge variant="outline" className={`text-[10px] ${b.cls}`}>{b.label}</Badge>
                  {it.status === "updated" && it.prior_filename && (
                    <span className="text-[10px] text-muted-foreground ml-1.5">replaced {it.prior_filename}</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[140px]">{it.uploaded_by_email}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProjectionSection({ projection, onPdf }) {
  if (!projection || !projection.rows?.length) return null;
  const t = projection.totals;
  const monthName = monthLabel(projection.month);
  return (
    <Card className="border-border shadow-none" data-testid="check-projection">
      <CardContent className="p-5 space-y-3">
        <div className="text-sm font-semibold flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Year-end projection
          <span className="text-xs font-normal text-muted-foreground">
            — if contributions stay as they are in {monthName} (× 12 months)
          </span>
          {onPdf && (
            <Button variant="outline" size="sm" className="ml-auto h-7" onClick={onPdf} data-testid="check-projection-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
            </Button>
          )}
        </div>
        {t && (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm flex flex-wrap gap-x-8 gap-y-1" data-testid="check-projection-totals">
            <span>Everyone combined by year end: <b>{fmt(t.annual_total)}</b></span>
            <span className="text-muted-foreground">Staff pay {fmt(t.annual_employee)} · Employer adds {fmt(t.annual_employer)}</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground bg-muted/30">
              <tr>
                <th className="text-left px-3 py-2">Person</th>
                <th className="text-right px-3 py-2">They pay / month</th>
                <th className="text-right px-3 py-2">Employer / month</th>
                <th className="text-right px-3 py-2">Total / month</th>
                <th className="text-right px-3 py-2 border-l border-border">They pay / year</th>
                <th className="text-right px-3 py-2">Employer / year</th>
                <th className="text-right px-3 py-2 font-semibold">In their pension by year end</th>
              </tr>
            </thead>
            <tbody>
              {projection.rows.map((r, i) => (
                <tr key={r.employee_name} className="border-t border-border" data-testid={`check-proj-row-${i}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {fmt(r.monthly_total)}/month now → {fmt(r.annual_total)} by year end
                    </div>
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(r.monthly_employee)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(r.monthly_employer)}</td>
                  <td className="text-right px-3 py-2 tabular-nums font-medium">{fmt(r.monthly_total)}</td>
                  <td className="text-right px-3 py-2 tabular-nums border-l border-border">{fmt(r.annual_employee)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(r.annual_employer)}</td>
                  <td className="text-right px-3 py-2 tabular-nums font-semibold">{fmt(r.annual_total)}</td>
                </tr>
              ))}
            </tbody>
            {t && (
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/20 font-semibold">
                  <td className="px-3 py-2">Total ({projection.rows.length} people)</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(t.monthly_employee)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(t.monthly_employer)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(t.monthly_total)}</td>
                  <td className="text-right px-3 py-2 tabular-nums border-l border-border">{fmt(t.annual_employee)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(t.annual_employer)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmt(t.annual_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Simple estimate: this month&apos;s contributions multiplied by 12. It doesn&apos;t account for pay changes, joiners/leavers or weeks-per-month differences.
        </p>
      </CardContent>
    </Card>
  );
}

function RangeChips({ summary }) {
  if (!summary?.months?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="check-range-summary">
      {summary.months.map((m) => {
        const hasPay = m.payroll_rows > 0;
        const hasZur = m.zurich_rows > 0;
        const ok = hasPay && hasZur;
        return (
          <span key={m.month}
            className={`text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1.5
              ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
            data-testid={`check-range-month-${m.month}`}>
            {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            <b>{monthLabel(m.month)}</b>
            <span>{hasPay ? `${m.weeks_covered}/${m.weeks_total} wks` : "no payslips"}</span>
            <span>·</span>
            <span>{hasZur ? "Zurich ✓" : "no Zurich"}</span>
          </span>
        );
      })}
    </div>
  );
}

function BreakdownPanel({ canonical, month, i }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let c = false;
    api.get(`/zurich-pension/breakdown?canonical=${encodeURIComponent(canonical)}&month=${month}`)
      .then((res) => { if (!c) setData(res.data); })
      .catch((e) => { if (!c) setErr(formatApiError(e)); });
    return () => { c = true; };
  }, [canonical, month]);
  if (err) return <div className="text-xs text-red-700 py-2">{err}</div>;
  if (!data) return (
    <div className="text-xs text-muted-foreground py-2 flex items-center gap-2">
      <Loader2 className="w-3 h-3 animate-spin" /> Loading breakdown…
    </div>
  );
  const wk = (x) => localDate(x).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 pb-1" data-testid={`check-breakdown-${i}`}>
      <div className="rounded border border-border bg-background/60 p-3">
        <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">
          Weekly payslips — {monthLabel(month)} ({data.payroll_rows.length})
        </div>
        {data.payroll_rows.length === 0 ? (
          <div className="text-xs text-muted-foreground">No payslip rows for this month.</div>
        ) : (
          <table className="w-full text-xs tabular-nums">
            <thead className="text-muted-foreground">
              <tr className="text-left"><th className="py-1 font-medium">Paid</th>
                <th className="py-1 font-medium text-right">They pay</th>
                <th className="py-1 font-medium text-right">Employer</th></tr>
            </thead>
            <tbody>
              {data.payroll_rows.map((r, j) => (
                <tr key={j} className="border-t border-border/60">
                  <td className="py-1">{wk(r.pay_date)}</td>
                  <td className="py-1 text-right">{fmt(r.employee_pension_deduction)}</td>
                  <td className="py-1 text-right">{fmt(r.employer_pension_contribution)}</td>
                </tr>
              ))}
              <tr className="border-t border-border font-semibold">
                <td className="py-1">Total</td>
                <td className="py-1 text-right">{fmt(data.payroll_employee_total)}</td>
                <td className="py-1 text-right">{fmt(data.payroll_employer_total)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      <div className="rounded border border-border bg-background/60 p-3">
        <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">
          Zurich collections for {monthLabel(month)} ({data.dd_rows.length})
        </div>
        {data.dd_rows.length === 0 ? (
          <div className="text-xs text-muted-foreground">Nothing collected by Zurich for this month.</div>
        ) : (
          <table className="w-full text-xs tabular-nums">
            <thead className="text-muted-foreground">
              <tr className="text-left"><th className="py-1 font-medium">Collected on</th>
                <th className="py-1 font-medium text-right">Employer</th>
                <th className="py-1 font-medium text-right">Employee</th>
                <th className="py-1 font-medium text-right">AVC</th>
                <th className="py-1 font-medium text-right">Total</th></tr>
            </thead>
            <tbody>
              {data.dd_rows.map((r, j) => (
                <tr key={j} className="border-t border-border/60">
                  <td className="py-1">
                    {wk(r.direct_debit_date)}
                    {String(r.dishonoured || "").toUpperCase().startsWith("Y") && (
                      <span className="ml-1 text-[10px] text-red-700 font-semibold">dishonoured</span>
                    )}
                  </td>
                  <td className="py-1 text-right">{fmt(r.employer_contribution)}</td>
                  <td className="py-1 text-right">{fmt(r.employee_contribution)}</td>
                  <td className="py-1 text-right">{fmt(r.avc_contribution)}</td>
                  <td className="py-1 text-right">{fmt(r.total)}</td>
                </tr>
              ))}
              <tr className="border-t border-border font-semibold">
                <td className="py-1">Total {data.dd_rows.length > 1 ? `(${data.dd_rows.length} collections)` : ""}</td>
                <td className="py-1" colSpan={3}></td>
                <td className="py-1 text-right">{fmt(data.dd_total)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function VarianceLegend() {
  return (
    <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="check-variance-legend">
      <span className="px-2 py-1 rounded border border-red-200 bg-red-50 text-red-800">
        <b>−€ (minus)</b> = Zurich collected LESS than the payslips deducted — collection still owed
      </span>
      <span className="px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-900">
        <b>+€ (plus)</b> = Zurich collected MORE than the payslips deducted
      </span>
      <span className="px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-900">
        <b>€0.00</b> = perfectly level
      </span>
    </div>
  );
}

function PersonDerivation({ r, months }) {
  const [openMonth, setOpenMonth] = useState(null);
  const active = months.filter((m) => r.monthly[m]);
  return (
    <div className="rounded border border-border bg-background/70 p-3 my-1.5 space-y-2" data-testid={`check-derivation-${r.canonical}`}>
      <div className="text-[11px] font-semibold text-muted-foreground">
        How {r.employee_name}&apos;s balance was worked out — month by month
      </div>
      <table className="w-full text-xs tabular-nums">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th className="py-1 font-medium">Month</th>
            <th className="py-1 font-medium text-right">Payslips deducted</th>
            <th className="py-1 font-medium text-right">Zurich collected</th>
            <th className="py-1 font-medium text-right">Difference</th>
            <th className="py-1 font-medium text-right">Running balance</th>
            <th className="py-1"></th>
          </tr>
        </thead>
        <tbody>
          {active.map((m) => {
            const v = r.monthly[m];
            const open = openMonth === m;
            return (
              <Fragment key={m}>
                <tr className="border-t border-border/60">
                  <td className="py-1 font-medium">{monthLabel(m)}</td>
                  <td className="py-1 text-right">{fmt(v.expected)}</td>
                  <td className="py-1 text-right">{fmt(v.collected)}</td>
                  <td className={`py-1 text-right ${v.variance > 0 ? "text-amber-700" : v.variance < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {fmt(v.variance)}
                  </td>
                  <td className="py-1 text-right font-semibold">{fmt(v.running_balance)}</td>
                  <td className="py-1 text-right">
                    <button
                      className="text-[11px] underline text-muted-foreground hover:text-foreground"
                      onClick={() => setOpenMonth(open ? null : m)}
                      data-testid={`check-derivation-detail-${m}`}>
                      {open ? "Hide detail" : "Show detail"}
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={6}>
                      <BreakdownPanel canonical={r.canonical} month={m} i={`deriv-${m}`} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BalanceRow({ r, i, months }) {
  const [open, setOpen] = useState(false);
  const cellCls = (v) => !v ? "" : v.variance > 0 ? "text-amber-700" : v.variance < 0 ? "text-red-700" : "text-emerald-700";
  return (
    <>
      <tr className="border-t border-border cursor-pointer hover:bg-muted/20"
        onClick={() => setOpen((o) => !o)} data-testid={`check-balance-row-${i}`}>
        <td className="px-3 py-1.5">
          <div className="font-medium text-sm flex items-center gap-1.5">
            {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
            {r.employee_name}
          </div>
          <div className="text-[10px] text-muted-foreground pl-5">
            {r.balance === 0
              ? "Level — everything collected"
              : r.balance > 0
                ? `Zurich has taken ${fmt(r.balance)} more than payslips so far`
                : `Zurich still owes collection of ${fmt(Math.abs(r.balance))}`}
          </div>
        </td>
        {months.map((m) => {
          const v = r.monthly[m];
          return (
            <td key={m} className={`text-right px-2 py-1.5 ${cellCls(v)}`}
              title={v ? `Expected ${fmt(v.expected)} · Collected ${fmt(v.collected)} · Running ${fmt(v.running_balance)}` : "No data"}>
              {v ? (v.variance === 0 ? "0" : fmt(v.variance)) : "–"}
            </td>
          );
        })}
        <td className={`text-right px-3 py-1.5 font-semibold border-l border-border ${r.balance > 0 ? "text-amber-700" : r.balance < 0 ? "text-red-700" : "text-emerald-700"}`}>
          {fmt(r.balance)}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/10">
          <td colSpan={months.length + 2} className="px-3 py-1">
            <PersonDerivation r={r} months={months} />
          </td>
        </tr>
      )}
    </>
  );
}

function BalancesSection({ balances }) {
  if (!balances) return null;
  const shortM = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return `${MONTH_NAMES[m - 1].slice(0, 3)} ${String(y).slice(2)}`;
  };
  const t = balances.totals;
  return (
    <Card className="border-border shadow-none" data-testid="check-balances">
      <CardContent className="p-5 space-y-3">
        <div className="text-sm font-semibold flex items-center gap-2">
          <Scale className="w-4 h-4" /> Running balances
          <span className="text-xs font-normal text-muted-foreground">
            — {monthLabel(balances.month_from)} to {monthLabel(balances.month_to)} · click a person to see exactly how their balance was worked out
          </span>
        </div>
        <VarianceLegend />
        {t && (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-2.5 text-sm flex flex-wrap gap-x-8 gap-y-1" data-testid="check-balances-totals">
            <span>Payslips expected <b>{fmt(t.expected)}</b></span>
            <span>Zurich collected <b>{fmt(t.collected)}</b></span>
            <span>Overall balance <b className={t.balance > 0 ? "text-amber-700" : t.balance < 0 ? "text-red-700" : "text-emerald-700"}>{fmt(t.balance)}</b></span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead className="text-muted-foreground bg-muted/30">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Person</th>
                {balances.months.map((m) => (
                  <th key={m} className="text-right px-2 py-2 font-medium whitespace-nowrap">{shortM(m)}</th>
                ))}
                <th className="text-right px-3 py-2 font-semibold border-l border-border">Balance now</th>
              </tr>
            </thead>
            <tbody>
              {balances.rows.map((r, i) => (
                <BalanceRow key={r.canonical} r={r} i={i} months={balances.months} />
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Click any person to see the month-by-month maths behind their balance, then &quot;Show detail&quot; on a month for the
          individual payslip rows and Zurich collections. People are sorted by the size of their outstanding balance —
          a −€ month followed by a +€ month usually means Zurich balanced it out later.
        </p>
      </CardContent>
    </Card>
  );
}

function ResultRow({ r, i }) {
  const ok = r.status === "matched";
  const red = RED.has(r.status);
  const [open, setOpen] = useState(false);
  return (
    <div className={`border-t border-border ${ok ? "" : red ? "bg-red-50/50" : "bg-amber-50/50"}`}>
      <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 cursor-pointer"
        onClick={() => setOpen((o) => !o)} data-testid={`check-row-${i}`}>
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <button className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
            data-testid={`check-row-toggle-${i}`}
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            : red ? <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            : <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />}
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">
              {r.employee_name}
              {r.showMonth && (
                <span className="ml-2 font-mono text-[10px] text-muted-foreground font-normal">· {monthLabel(r.month)}</span>
              )}
            </div>
            {!ok && (
              <>
                <div className="text-xs text-muted-foreground mt-0.5">{r.reason}</div>
                {r.action_required && (
                  <div className="text-xs mt-0.5"><b>What to do:</b> {r.action_required}</div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs tabular-nums shrink-0 pl-6 sm:pl-0">
          <div className="text-right">
            <div className="text-muted-foreground">Payslips say</div>
            <div className="font-medium">{fmt(r.expected_total_with_adjustment ?? r.expected_total_contribution)}</div>
          </div>
          <div className="text-right">
            <div className="text-muted-foreground">Zurich took</div>
            <div className="font-medium">{fmt(r.dd_total)}</div>
          </div>
          <div className="text-right w-20">
            <div className="text-muted-foreground">Difference</div>
            <div className={`font-semibold ${!r.variance ? "" : r.variance > 0 ? "text-amber-700" : "text-red-700"}`}>
              {typeof r.variance === "number" ? fmt(r.variance) : "—"}
            </div>
          </div>
          <Badge variant={ok ? "default" : "outline"} className={ok ? "bg-emerald-600" : red ? "border-red-300 text-red-800" : "border-amber-300 text-amber-800"}>
            {STATUS_LABELS[r.status] || r.status}
          </Badge>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-3 pl-11">
          <BreakdownPanel canonical={r.employee_name_canonical} month={r.month} i={i} />
        </div>
      )}
    </div>
  );
}

export default function PensionCheckPage() {
  const [month, setMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [run, setRun] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [review, setReview] = useState(null);
  const [busyPayslips, setBusyPayslips] = useState(false);
  const [busyZurich, setBusyZurich] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [log, setLog] = useState([]);
  const [projection, setProjection] = useState(null);
  const [mode, setMode] = useState("single");
  const [monthTo, setMonthTo] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [rangeSummary, setRangeSummary] = useState(null);
  const [zurichStored, setZurichStored] = useState(0);
  const [balances, setBalances] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const skipResumeRef = useRef(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/pension-close");
      setHistory(data?.items || []);
    } catch { /* ignore */ }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const { data } = await api.get("/payroll-library/log?limit=10");
      setLog(data?.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); loadLog(); }, [loadHistory, loadLog]);

  const refreshCoverage = useCallback(async (m) => {
    try {
      const { data } = await api.get(`/payroll-library/coverage?month=${m}`);
      setCoverage(data);
      if (data.weeks_covered > 0) {
        try {
          const { data: proj } = await api.get(`/payroll-library/projection?month=${m}`);
          setProjection(proj);
        } catch { setProjection(null); }
      } else {
        setProjection(null);
      }
      try {
        const { data: rs } = await api.get(`/payroll-library/range-summary?month_from=${m}&month_to=${m}`);
        setZurichStored(rs?.months?.[0]?.zurich_rows || 0);
      } catch { setZurichStored(0); }
      return data;
    } catch { setCoverage(null); setProjection(null); return null; }
  }, []);

  // Coverage + draft (Zurich) recall whenever the month changes.
  useEffect(() => {
    let cancelled = false;
    refreshCoverage(month);
    if (skipResumeRef.current) { skipResumeRef.current = false; return; }
    (async () => {
      try {
        const { data } = await api.get("/pension-close");
        if (cancelled) return;
        const draft = (data?.items || []).find((r) => r.month === month && r.status === "in_progress");
        if (draft) {
          setRun(draft);
          if (draft.zurich_file_id) {
            toast.info(`Found the Zurich file you already uploaded for ${month}.`);
          }
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [month, refreshCoverage]);

  // Range summary whenever the range or mode changes.
  useEffect(() => {
    if (mode !== "range") { setRangeSummary(null); return; }
    const [ay, am] = month.split("-").map(Number);
    const [by, bm] = monthTo.split("-").map(Number);
    const span = (by - ay) * 12 + (bm - am);
    if (span < 0 || span > 11) { setRangeSummary(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/payroll-library/range-summary?month_from=${month}&month_to=${monthTo}`);
        if (!cancelled) setRangeSummary(data);
      } catch { if (!cancelled) setRangeSummary(null); }
    })();
    return () => { cancelled = true; };
  }, [mode, month, monthTo]);

  const ensureRun = async () => {
    if (run && run.month === month && run.status === "in_progress") return run;
    const { data } = await api.post("/pension-close/start", { month });
    setRun(data.run);
    return data.run;
  };

  const changeMonth = (m) => {
    setMonth(m); setRun(null); setCoverage(null); setReview(null); setProjection(null); setBalances(null);
  };

  const loadBalances = async () => {
    const from = mode === "range" ? month : monthShift(month, -5);
    const to = mode === "range" ? monthTo : month;
    setBalancesLoading(true);
    try {
      const { data } = await api.get(`/zurich-pension/balances?month_from=${from}&month_to=${to}`);
      if (!data.rows.length) toast.info("No pension data found for that period yet.");
      setBalances(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBalancesLoading(false); }
  };

  const uploadPayslips = async (files) => {
    setBusyPayslips(true);
    try {
      let added = 0, dup = 0, updated = 0;
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const { data } = await api.post("/payroll-library/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const st = data.status;
          if (st === "duplicate") {
            dup++;
            toast.info(`${f.name}: duplicate — you already uploaded this file, skipped.`);
          } else if (st === "updated") {
            updated++;
            toast.warning(`${f.name}: updated — replaced the earlier version of this week (${data.log?.rows || 0} rows).`);
          } else {
            added++;
            toast.success(`${f.name}: added — ${data.log?.rows || 0} rows stored.`);
          }
        } catch (e) {
          toast.error(`${f.name}: ${formatApiError(e)}`);
        }
      }
      if (files.length > 1) {
        toast.info(`Done: ${added} added · ${updated} updated · ${dup} duplicates skipped.`);
      }
      loadLog();
      refreshCoverage(month);
      setReview(null);
    } finally { setBusyPayslips(false); }
  };

  const uploadZurich = async (files) => {
    setBusyZurich(true);
    try {
      const r = await ensureRun();
      const fd = new FormData();
      fd.append("file", files[0]);
      const { data } = await api.post(`/pension-close/${r.id}/zurich`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const replaced = data.replaced_overlapping_rows || 0;
      toast.success(`Zurich file: ${data.inserted_rows || 0} rows read${replaced ? ` — replaced ${replaced} older rows for the same months` : ""}`);
      const fresh = await api.get(`/pension-close/${r.id}`);
      setRun(fresh.data);
      setReview(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyZurich(false); }
  };

  const askCompare = async () => {
    if (mode === "range") {
      const span = (() => {
        const [ay, am] = month.split("-").map(Number);
        const [by, bm] = monthTo.split("-").map(Number);
        return (by - ay) * 12 + (bm - am);
      })();
      if (span < 0) return toast.error("'From' month must not be after 'To' month");
      if (span > 11) return toast.error("Range too large — maximum 12 months");
      const months = rangeSummary?.months || [];
      if (!months.some((m) => m.payroll_rows > 0)) return toast.error("No payslips in the library for any month in this range");
      if (!months.some((m) => m.zurich_rows > 0)) return toast.error("No Zurich data stored for this range — upload the Zurich file first");
      setConfirmOpen(true);
      return;
    }
    if (!coverage || coverage.weeks_covered === 0) return toast.error(`No payslips found for ${month} — upload them to the library first`);
    if (!run?.zurich_file_id && !zurichStored) return toast.error("Upload the Zurich file first");
    await refreshCoverage(month);
    setConfirmOpen(true);
  };

  const runCompare = async () => {
    setConfirmOpen(false);
    setComparing(true);
    try {
      if (mode === "range") {
        const { data } = await api.get(`/zurich-pension/reconciliation?month_from=${month}&month_to=${monthTo}`);
        setReview({ ...data, isRange: true, from: month, to: monthTo });
      } else if (run?.zurich_file_id) {
        const { data } = await api.get(`/pension-close/${run.id}/review`);
        setReview(data);
        loadHistory();
      } else {
        // Zurich data already stored in the library for this month — no run file needed
        const { data } = await api.get(`/zurich-pension/reconciliation?month_from=${month}&month_to=${month}`);
        setReview({ ...data, isRange: true, from: month, to: month });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setComparing(false); }
  };

  const openHistoryRun = async (item) => {
    try {
      const { data } = await api.get(`/pension-close/${item.id}`);
      skipResumeRef.current = data.month !== month;
      setMonth(data.month);
      setRun(data);
      setReview(null);
      refreshCoverage(data.month);
      if (data.zurich_file_id) {
        setComparing(true);
        try {
          const r = await api.get(`/pension-close/${data.id}/review`);
          setReview(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setComparing(false); }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (kind) => {
    try {
      const url = review?.isRange
        ? `/zurich-pension/exports/reconciliation.${kind}?month_from=${review.from}&month_to=${review.to}`
        : `/pension-close/${run.id}/${kind}`;
      const name = review?.isRange
        ? `pension-check-${review.from}_to_${review.to}.${kind}`
        : `pension-check-${run.month}.${kind}`;
      const res = await api.get(url, { responseType: "blob" });
      const type = kind === "pdf" ? "application/pdf" : "text/csv";
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = name; a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadProjectionPdf = async () => {
    try {
      const res = await api.get(`/payroll-library/projection.pdf?month=${projection.month}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = `pension-projection-${projection.month}.pdf`; a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const summary = review?.summary;
  const rows = review?.rows || [];
  const needAttention = summary ? (summary.rows - summary.matched) : 0;
  const sortedRows = [...rows].sort((a, b) =>
    ((a.status === "matched") - (b.status === "matched")) ||
    String(a.month || "").localeCompare(String(b.month || "")) ||
    String(a.employee_name || "").localeCompare(String(b.employee_name || "")));
  const allGood = summary && needAttention === 0;
  const monthCovered = (coverage?.weeks_covered || 0) > 0;
  const rangeSpan = (() => {
    const [ay, am] = month.split("-").map(Number);
    const [by, bm] = monthTo.split("-").map(Number);
    return (by - ay) * 12 + (bm - am);
  })();
  const rangeInvalid = mode === "range"
    ? (rangeSpan < 0 ? "'From' month must not be after 'To' month"
      : rangeSpan > 11 ? "Range too large — maximum 12 months" : null)
    : null;
  const rangeReady = !!(rangeSummary?.months?.some((m) => m.payroll_rows > 0) &&
    rangeSummary?.months?.some((m) => m.zurich_rows > 0));
  const canCompareSingle = monthCovered && (!!run?.zurich_file_id || zurichStored > 0);
  const compareDisabled = comparing || (mode === "range" ? (!!rangeInvalid || !rangeReady) : !canCompareSingle);

  return (
    <div className="space-y-6" data-testid="pension-check-page">
      <div>
        <div className="tiny-label">Pension · quick check</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Pension check</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Drop payslips into the library any time — duplicates are skipped automatically. Then pick a month, add the Zurich file and compare.
        </p>
      </div>

      {/* Step 1 — Payslip library */}
      <Card className="border-border shadow-none">
        <CardContent className="p-5 space-y-4">
          <div className="text-sm font-semibold flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-foreground text-background grid place-items-center text-[11px]">1</span>
            <Library className="w-4 h-4" /> Payslip library
            <span className="text-xs font-normal text-muted-foreground">— upload any weeks, any time</span>
          </div>
          <DropZone
            title="Drop weekly payslip files here (or click)"
            subtitle="Drop them ALL at once — new weeks are added, exact duplicates skipped, changed weeks replaced"
            accept=".xlsx,.xls,.csv" multiple onFiles={uploadPayslips}
            done={false} doneText="" busy={busyPayslips} testId="check-payslip-drop" />
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium text-muted-foreground">Upload record (last 10)</div>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={loadLog} data-testid="check-log-refresh">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>
            <UploadLog items={log} />
          </div>
        </CardContent>
      </Card>

      {/* Step 2 — Month + Zurich */}
      <Card className="border-border shadow-none">
        <CardContent className="p-5 space-y-4">
          <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
            <span className="w-5 h-5 rounded-full bg-foreground text-background grid place-items-center text-[11px]">2</span>
            Pick the month &amp; add the Zurich file
            <div className="ml-auto flex rounded-md border border-border overflow-hidden text-xs font-medium">
              <button
                className={`px-3 py-1.5 ${mode === "single" ? "bg-foreground text-background" : "hover:bg-muted/50"}`}
                onClick={() => { setMode("single"); setReview(null); }}
                data-testid="check-mode-single">
                Single month
              </button>
              <button
                className={`px-3 py-1.5 border-l border-border ${mode === "range" ? "bg-foreground text-background" : "hover:bg-muted/50"}`}
                onClick={() => { setMode("range"); setReview(null); }}
                data-testid="check-mode-range">
                Month range
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-sm font-medium">{mode === "range" ? "From:" : "Month to check:"}</div>
            <MonthYearPicker value={month} onChange={changeMonth}
              monthTestId="check-month-select" yearTestId="check-year-select" />
            {mode === "range" && (
              <>
                <div className="text-sm font-medium">To:</div>
                <MonthYearPicker value={monthTo} onChange={(m) => { setMonthTo(m); setReview(null); }}
                  monthTestId="check-month-to-select" yearTestId="check-year-to-select" />
              </>
            )}
            {mode === "single" && run?.status === "finalised" && <Badge>finalised</Badge>}
          </div>
          {mode === "single" ? (
            <>
              <WeekStrip coverage={coverage} />
              {!monthCovered && (
                <div className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="check-no-payslips-hint">
                  <AlertTriangle className="w-3.5 h-3.5" /> No payslips in the library for this month yet — add them in step 1.
                </div>
              )}
            </>
          ) : (
            <>
              {rangeInvalid && (
                <div className="text-xs text-red-700 flex items-center gap-1.5" data-testid="check-range-error">
                  <XCircle className="w-3.5 h-3.5" /> {rangeInvalid}
                </div>
              )}
              <RangeChips summary={rangeSummary} />
            </>
          )}
          <DropZone
            title="Drop the Zurich CSV here (or click)"
            subtitle={mode === "range"
              ? "The Zurich file usually covers several months — upload it once and every month in the range uses it"
              : `The monthly direct-debit file from Zurich for ${month}`}
            accept=".csv" multiple={false} onFiles={uploadZurich}
            done={!!run?.zurich_file_id || zurichStored > 0}
            doneText={run?.zurich_file_id
              ? "Zurich file attached — drop again to replace"
              : `Zurich data for ${monthLabel(month)} is already stored — drop a file only to replace it`}
            busy={busyZurich} testId="check-zurich-drop" />
          {mode === "single" && !run?.zurich_file_id && zurichStored > 0 && (
            <div className="text-xs flex items-start gap-1.5 text-muted-foreground" data-testid="check-zurich-stored-note">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
              <span>
                <b>{zurichStored}</b> Zurich payment{zurichStored === 1 ? "" : "s"} already stored for {monthLabel(month)} — no upload needed,
                just hit Compare. Dropping a new file will <b>replace</b> the stored data for every month the new file covers.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — Compare */}
      <div className="flex items-center gap-3">
        <span className="w-5 h-5 rounded-full bg-foreground text-background grid place-items-center text-[11px]">3</span>
        <Button size="lg" onClick={askCompare}
          disabled={compareDisabled}
          data-testid="check-compare-btn">
          {comparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scale className="w-4 h-4 mr-2" />}
          {mode === "range"
            ? `Compare ${monthLabel(month)} – ${monthLabel(monthTo)}`
            : "Compare payslips vs Zurich"}
        </Button>
        {mode === "single" && !canCompareSingle && (
          <span className="text-xs text-muted-foreground">
            {!monthCovered ? "No payslips stored for this month yet" : "Upload the Zurich file first"}
          </span>
        )}
        {mode === "range" && !rangeReady && (
          <span className={`text-xs ${rangeInvalid ? "text-red-700" : "text-muted-foreground"}`}>
            {rangeInvalid || "Needs at least one month with payslips and Zurich data in the range"}
          </span>
        )}
        <Button size="lg" variant="outline" onClick={loadBalances}
          disabled={balancesLoading || (mode === "range" && !!rangeInvalid)}
          data-testid="check-balances-btn">
          {balancesLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
          Running balances
        </Button>
      </div>

      {/* Results */}
      {review && summary && (
        <div className="space-y-4" data-testid="check-results">
          <div className={`rounded-lg border p-4 flex items-center gap-3
            ${allGood ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
            data-testid="check-verdict">
            {allGood
              ? <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
              : <AlertTriangle className="w-8 h-8 text-amber-600 shrink-0" />}
            <div>
              <div className="font-display font-bold text-lg">
                {(() => {
                  const label = review.isRange
                    ? (review.from === review.to ? monthLabel(review.from) : `${monthLabel(review.from)} – ${monthLabel(review.to)}`)
                    : (review.month || run?.month);
                  return allGood
                    ? `Everything matches for ${label} ✓`
                    : `${needAttention} ${needAttention === 1 ? "person needs" : "people need"} attention (${label})`;
                })()}
              </div>
              <div className="text-sm text-muted-foreground">
                {summary.matched} of {summary.rows} people match · Payslips expected {fmt(summary.total_payroll_expected)} · Zurich took {fmt(summary.total_zurich_debit)} · Difference {fmt(summary.net_variance)}
              </div>
            </div>
            <div className="ml-auto flex gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="check-pdf">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="check-csv">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            </div>
          </div>

          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <div className="px-4 py-2.5 text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <HelpCircle className="w-3.5 h-3.5" />
                Problems are listed first. Green rows are fine — no action needed.
              </div>
              <div className="px-4 pb-2.5">
                <VarianceLegend />
              </div>
              {sortedRows.map((r, i) => (
                <ResultRow key={`${r.employee_name_canonical}-${r.month}-${i}`}
                  r={review?.isRange && review.from !== review.to ? { ...r, showMonth: true } : r} i={i} />
              ))}
              {sortedRows.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground border-t border-border">
                  No data found for this month in either file.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Running balances */}
      <BalancesSection balances={balances} />

      {/* Year-end projection (single-month view only) */}
      {mode === "single" && <ProjectionSection projection={projection} onPdf={downloadProjectionPdf} />}

      {/* History */}
      {history.length > 0 && (
        <div className="pt-2">
          <div className="text-sm font-semibold flex items-center gap-2 mb-2">
            <History className="w-4 h-4" /> Past checks
          </div>
          <div className="flex flex-wrap gap-2">
            {[...history]
              .sort((a, b) =>
                ((b.status === "in_progress") - (a.status === "in_progress")) ||
                String(b.updated_at || "").localeCompare(String(a.updated_at || "")))
              .slice(0, 8).map((h) => (
              <button key={h.id} onClick={() => openHistoryRun(h)}
                className="px-3 py-1.5 rounded border border-border hover:bg-muted/40 text-sm flex items-center gap-2"
                data-testid={`check-history-${h.month}`}>
                <span className="font-mono text-xs">{h.month}</span>
                <Badge variant={h.status === "finalised" ? "default" : "outline"} className="text-[10px]">
                  {h.status === "finalised" ? "finalised" : "draft"}
                </Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="check-confirm-dialog">
          {mode === "range" ? (
            <>
              <DialogHeader>
                <DialogTitle>Compare {monthLabel(month)} – {monthLabel(monthTo)}?</DialogTitle>
                <DialogDescription>
                  Every month in the range is compared using the payslips and Zurich data already stored.
                  Months without payslips will show their Zurich debits as unexpected.
                </DialogDescription>
              </DialogHeader>
              <RangeChips summary={rangeSummary} />
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Have you uploaded ALL payslips for {month}?</DialogTitle>
                <DialogDescription>
                  {coverage
                    ? `${coverage.weeks_covered} of ${coverage.weeks_total} weeks have payslip data${coverage.gap_count > 0 ? ` — ${coverage.gap_count} week${coverage.gap_count === 1 ? " is" : "s are"} missing` : ""}.`
                    : "We couldn't detect the week coverage."}
                  {(!coverage || coverage.gap_count > 0) && " Missing weeks will make people look under-paid in the comparison."}
                </DialogDescription>
              </DialogHeader>
              <WeekStrip coverage={coverage} />
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="check-confirm-no">
              {mode === "range" ? "Cancel" : "Not yet — add more"}
            </Button>
            <Button onClick={runCompare} data-testid="check-confirm-yes">
              Yes, compare now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
